/** @file Built-in yaw sector tables (design 4.3, 17.3), oriented so azimuth 0 faces the camera. */
import { sectorCentre } from '@zilo/pose3d-format/tables/sectorCentre';
import { sectorOf } from '@zilo/pose3d-format/tables/sectorOf';
import type { SectorTable } from '@zilo/pose3d-format/types/facet';

/** The original's uneven 4- and 8-sector thresholds, and even 16 sectors. */
const T4 = [40, 140, 220, 320];
const T8 = [25, 70, 115, 160, 200, 245, 290, 335];
const T16 = Array.from({ length: 16 }, (_, i) => 11.25 + 22.5 * i);
const f = false;
const t = true;
const NO_FLIP_8 = [f, f, f, f, f, f, f, f];
const NO_FLIP_16 = [...NO_FLIP_8, ...NO_FLIP_8];
const SIDE_BACK_4: (0 | 1 | 2)[] = [0, 1, 2, 1];
const SIDE_BACK_8: (0 | 1 | 2)[] = [0, 0, 1, 2, 2, 2, 1, 0];
const SIDE_BACK_16: (0 | 1 | 2)[] = [0, 0, 0, 0, 1, 2, 2, 2, 2, 2, 2, 2, 1, 0, 0, 0];
const range = (n: number) => Array.from({ length: n }, (_, i) => i);

function table(id: string, thresholds: number[], tile: number[], flip: boolean[], sideBack: (0 | 1 | 2)[]): SectorTable {
  return { id, thresholds: [...thresholds], tile: [...tile], flip: [...flip], sideBack: [...sideBack] };
}

const BASE: SectorTable[] = [
  table('FACE_1', [360], [0], [f], [0]),
  table('FACE_2', [180, 360], [0, 1], [f, f], [1, 1]),
  table('MIRROR_2', [180, 360], [0, 0], [f, t], [1, 1]),
  table('FRONT_BACK_2', [90, 270], [0, 1], [f, f], [0, 2]),
  table('MIRROR_4', T4, [0, 1, 2, 1], [f, f, f, t], SIDE_BACK_4),
  table('FACE_4', T4, range(4), [f, f, f, f], SIDE_BACK_4),
  table('MIRROR_8', T8, [0, 1, 2, 3, 4, 3, 2, 1], [f, f, f, f, f, t, t, t], SIDE_BACK_8),
  table('FACE_8', T8, range(8), NO_FLIP_8, SIDE_BACK_8),
  table('MIRROR_16', T16, [0, 1, 2, 3, 4, 5, 6, 7, 8, 7, 6, 5, 4, 3, 2, 1], [...NO_FLIP_8, f, t, t, t, t, t, t, t], SIDE_BACK_16),
  table('FACE_16', T16, range(16), NO_FLIP_16, SIDE_BACK_16),
  table('QUAD_8', T8, [0, 1, 2, 1, 0, 1, 2, 1], [f, f, f, t, f, f, f, t], SIDE_BACK_8),
  table('QUAD_16', T16, [0, 1, 2, 3, 4, 3, 2, 1, 0, 1, 2, 3, 4, 3, 2, 1], [f, f, f, f, f, t, t, t, f, f, f, f, f, t, t, t], SIDE_BACK_16),
  table('FRONT_ONLY_8', T8, [0, 1, 2, -1, -1, -1, 3, 4], NO_FLIP_8, SIDE_BACK_8),
  table('FRONT_ONLY_16', T16, [0, 1, 2, 3, 4, -1, -1, -1, -1, -1, -1, -1, 5, 6, 7, 8], NO_FLIP_16, SIDE_BACK_16),
];

/** The twin side's table: sector s looks up the mirrored azimuth (360 - centre) and flips that image. */
function flipTwin(base: SectorTable): SectorTable {
  const from = base.thresholds.map((_, s) => sectorOf(360 - sectorCentre(base, s), base));
  const tile = from.map((j) => base.tile[j] ?? -1);
  const flip = from.map((j, s) => (tile[s] ?? -1) >= 0 && base.flip[j] !== true);
  const sideBack = from.map((j) => base.sideBack[j] ?? 0);
  return table(`${base.id}_FLIP`, base.thresholds, tile, flip, sideBack);
}

/**
 * Built-in tables by id. Tile 0 is drawn at azimuth 0 (facing the camera) and drawn tiles go clockwise;
 * MIRROR tables draw azimuths 0..180 and flip the rest; FRONT_ONLY tables draw nothing (-1) from behind;
 * QUAD tables repeat every 180 degrees; `<id>_FLIP` is the twin side of every table with 4 or more sectors.
 * FACE_2 / MIRROR_2 are left/right, FRONT_BACK_2 front/back. sideBack: 0 front, 1 side, 2 back.
 */
export const SECTOR_TABLES: Readonly<Record<string, SectorTable>> = Object.fromEntries(
  [...BASE, ...BASE.filter((b) => b.thresholds.length >= 4).map(flipTwin)].map((tb) => [tb.id, tb]),
);
